import { fork, takeEvery, cancel, call, select, put, all } from 'redux-saga/effects';
import routes from '@/router/routes';
import routeSagas from '@/router/routeSagas';
import router from '@/router/router';
import initLocationChangeListener from '@/domain/router/redux/locationChangeListener';
import { completeAction, noLocationChange } from '@/domain/router/RouterActions';
import { hasUserRequireDataSelector, getUserDataComplete } from '@/domain/env';
import { locationIsChange, locationSelector } from '@/domain/router/RouterSelector';
import { LOCATION_CHANGE, locationChangeAction } from '@/domain/router/redux/reduxActions';
import { matchRoutes } from 'react-router-dom';
import { ToastEventHandler } from '@/components/Tables/toast';

// State management
// TODO: module state outlives a restart of the navigator saga; it belongs in the saga's closure.
let activeTasks = [];

const createCompleteCallback = (locationKey) =>
  function* unnamedCompleteCallback() {
    yield put(completeAction(locationKey));
  };

// Task management functions
function* cancelAllActiveTasks() {
  if (activeTasks.length > 0) {
    // Use array map with all() for parallel cancellation
    yield all(activeTasks.map((task) => cancel(task)));
    activeTasks = [];
    yield put(noLocationChange());
  }
}

function* executeSaga(routeSaga, params, location, dispatch) {
  // Load and execute saga
  const sagas = yield call(routeSaga.saga);
  const complete = createCompleteCallback(location.key);

  // Forked as an attached child of this wrapper task: cancelling the wrapper
  // (tracked in activeTasks) cancels this route saga and its watchers too.
  yield fork(sagas.default, location, { params }, complete, dispatch, routeSaga.sagasProps);
}

// Main processing function
function* processMatchedRoutes(matchedRoutes, location, dispatch) {
  let sagaFound = false;

  // Process routes recursively from level 1 (skip App wrapper)
  for (let level = 1; level < matchedRoutes.length; level++) {
    const { route, params } = matchedRoutes[level];
    const routeSaga = routeSagas[route.id];

    if (routeSaga) {
      sagaFound = true;
      // Register the wrapper task SYNCHRONOUSLY — before executeSaga awaits the
      // dynamic import() of the route saga chunk. Otherwise a second, rapid
      // navigation (e.g. a redirect that appends ?flowState=...) runs
      // cancelAllActiveTasks while activeTasks is still empty, fails to cancel
      // this in-flight load, and both navigations end up forking the same route
      // saga — registering its takeLatest watchers twice, so one dispatched
      // action triggers two HTTP requests.
      const task = yield fork(executeSaga, routeSaga, params, location, dispatch);
      activeTasks.push(task);
    }
  }

  return sagaFound;
}

// Navigation handler
function* handleNavigation({ payload, dispatch }) {
  // TODO: runs on every router state update, not only on a location change — an unsaved-changes
  // prompt opening or being cancelled too — so those also close every toast and put
  // noLocationChange. Both belong after the location check.
  ToastEventHandler.closeAll();

  const hasUserRequireData = yield select(hasUserRequireDataSelector);
  // TODO: compares the store's prevLocation, which every LOCATION_CHANGE overwrites, blocker updates
  // included. A location the navigator itself last handled (pathname, search and key — keys alone
  // repeat as "default") would not depend on the store.
  const isLocationChange = yield select(locationIsChange);
  const location = 'location' in payload ? payload.location : payload;

  if (!isLocationChange || !hasUserRequireData) {
    yield put(noLocationChange());
    return;
  }

  const matchedRoutes = matchRoutes(routes, location);
  if (!matchedRoutes) {
    yield put(noLocationChange());
    return;
  }

  // Cancel existing tasks and process new routes
  yield call(cancelAllActiveTasks);
  const sagaFound = yield call(processMatchedRoutes, matchedRoutes, location, dispatch);

  if (!sagaFound) {
    console.log('invoking complete as no sagas found in route hierarchy');
    yield put(completeAction(location.key));
  }
}

// Export functions
// TODO: dead — nothing dispatches getUserDataComplete. A navigation skipped while the user's data
// is missing is never retried; the navigator is correct only because it starts after the session
// is restored.
export function* afterInit(dispatch) {
  const payload = yield select(locationSelector);
  yield call(handleNavigation, { payload, dispatch });
}

export default function* navigator(dispatch) {
  const locationChangeListener = initLocationChangeListener(router, dispatch);

  yield takeEvery(LOCATION_CHANGE, ({ payload }) => handleNavigation({ payload, dispatch }));

  yield takeEvery(getUserDataComplete.type, () => afterInit(dispatch));
  yield put(locationChangeAction(router.state.location, router.state.historyAction));
  yield call(locationChangeListener);
}
